import { toastTypes } from 'components/Toast'
import { useTranslation } from 'contexts/Localization'
import useToast from 'hooks/useToast'
import { MutableRefObject, useCallback, useState, useEffect } from 'react'
import { useGotchiAuth } from 'state/vvsgotchi/hooks/useAuth'
import { Mole } from 'state/vvsgotchi/models/mole'
import { UpdateMoleMutation } from 'vvs-gotchi-graphql'
import { Text, InjectedModalProps, Flex, UserDisabledIcon } from 'vvs-uikit'
import ActionButton from '../components/ActionButton'
import { LoadingView } from '../components/LoadingView'
import { GotchiModal } from '../components/Modal'
import { MAX_MOLE_NAME_LENGTH, MIN_MOLE_NAME_LENGTH, MIN_MOLE_NAME_INVALID, getUpdateMoleErrorsMsg } from '../config'
import { StyledInput } from './styles'

type UpdateMoleErrorsType = UpdateMoleMutation['updateMole']['errors']

interface RenameModalProps extends InjectedModalProps {
  mole: Mole
  onActionClick: (value: string) => Promise<void>
  renameErrors: UpdateMoleErrorsType
  renameErrorsRef: MutableRefObject<UpdateMoleErrorsType>
  reset: () => void
  loading: boolean
  refresh: () => Promise<void>
}
export const RenameModal: React.FC<RenameModalProps> = ({
  mole,
  onActionClick,
  onDismiss,
  reset,
  renameErrors,
  renameErrorsRef,
  loading,
  refresh,
}) => {
  const { t } = useTranslation()
  const [value, setValue] = useState<string>('')
  const [, ensureAuth] = useGotchiAuth()
  const { toastVVSGotchi } = useToast()

  const handleOnChange = (e) => {
    setValue(e.target.value)
    reset()
  }

  const handleDismiss = useCallback(() => {
    onDismiss()
    reset()
  }, [onDismiss, reset])

  const wrappedAction = useCallback(
    async (newName: string) => {
      await ensureAuth()

      try {
        await onActionClick(newName)
        if (!renameErrorsRef.current || renameErrorsRef.current.length === 0) {
          handleDismiss()
          await refresh()
          toastVVSGotchi(
            t('Successfully renamed your Miner Mole ( ͡° ͜ʖ ͡° )'),
            toastTypes.INFO,
            <UserDisabledIcon fill="black" width="16px" />,
          )
        }
      } catch (e) {
        console.error(e)
      }
    },
    [onActionClick, handleDismiss, renameErrorsRef, t, toastVVSGotchi, ensureAuth, refresh],
  )

  useEffect(() => {
    if (mole?.name) {
      setValue(mole?.name)
    }
  }, [mole?.name])

  return (
    <GotchiModal onDismiss={handleDismiss}>
      {!loading && (
        <>
          <Text color="black" fontSize="20px" fontWeight="600" mb="8px">
            {t('What is Mole’s name?')}
          </Text>
          <StyledInput
            value={value}
            onChange={handleOnChange}
            autoComplete="off"
            autoCorrect="off"
            type="text"
            minLength={1}
            maxLength={79}
            spellCheck="false"
          />

          {renameErrors && renameErrors.length === 1 && (
            <Flex>
              <Text color="failure">{getUpdateMoleErrorsMsg(renameErrors[0], t)}</Text>
            </Flex>
          )}
          <ActionButton mt="16px" onClick={() => wrappedAction(value)} disabled={isMoleNameInvalid(value, mole)}>
            {t('Name It')}
          </ActionButton>
          <ActionButton mt="16px" onClick={() => wrappedAction(`#${mole.tokenId}`)} disabled={!mole}>
            {mole ? t('Reset to %defaultName%', { defaultName: `#${mole.tokenId}` }) : t('Reset')}
          </ActionButton>
        </>
      )}
      {loading && <LoadingView>{t('Loading...')}</LoadingView>}
    </GotchiModal>
  )
}

const isMoleNameInvalid = (value: string, currentMole: Mole): boolean =>
  !value ||
  value === currentMole?.name ||
  value.length < MIN_MOLE_NAME_LENGTH ||
  value.length > MAX_MOLE_NAME_LENGTH ||
  !!value.match(MIN_MOLE_NAME_INVALID)
